import { Suspense, lazy, useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { tokens } from "@/app/application/theme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Alert,
	Box,
	Button,
	Collapse,
	IconButton,
	Snackbar,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


const ReportBugDialog = lazy(() =>
	import("@/app/application/ReportBugDialog").then((m) => ({
		default: m.ReportBugDialog,
	})),
);

type Props = {
	error: unknown;
	componentStack: string;
	reset: () => void;
};

const PageError = ({ error, componentStack, reset }: Props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const navigate = useNavigate();
	const tryAgainRef = useRef<HTMLButtonElement>(null);
	const [showDetails, setShowDetails] = useState(false);
	const [copied, setCopied] = useState(false);
	const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
	const [bugDialogOpen, setBugDialogOpen] = useState(false);

	const errorMessage =
		error instanceof Error ? error.message : String(error ?? "");
	const detailsText =
		errorMessage && componentStack
			? `${errorMessage}\n\n${componentStack}`
			: errorMessage || componentStack || "No additional details available.";
	const bugReportContext = `Page error at ${typeof window !== "undefined" ? window.location.href : ""}\n\nError: ${errorMessage}${componentStack ? `\n\nStack:\n${componentStack}` : ""}`;

	useEffect(() => {
		tryAgainRef.current?.focus();
	}, []);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (bugDialogOpen) return;
			if (e.key === "r" || e.key === "R") {
				e.preventDefault();
				reset();
			} else if (e.key === "Escape") {
				if (window.history.length > 1) {
					e.preventDefault();
					window.history.back();
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [reset, bugDialogOpen]);

	const handleGoBack = () => {
		if (window.history.length > 1) {
			window.history.back();
		} else {
			navigate("/");
		}
	};

	const handleCopy = async () => {
		const url = typeof window !== "undefined" ? window.location.href : "";
		const text = `URL: ${url}\n\n--- Developer details ---\n${detailsText}`;
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setCopySnackbarOpen(true);
			setTimeout(() => {
				setCopied(false);
				setCopySnackbarOpen(false);
			}, 2500);
		} catch {
			setCopied(false);
		}
	};

	const contentMaxWidth = 520;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				minHeight: "100vh",
				height: "100vh",
				boxSizing: "border-box",
				backgroundColor: (t) => t.palette.background.paper,
				overflowY: "auto",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					flex: "1 1 auto",
					width: "100%",
					maxWidth: contentMaxWidth,
					p: 4,
					textAlign: "center",
				}}
			>
				{/* Main content: icon, title, message, try again */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						width: "100%",
						py: 4,
					}}
				>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: 64,
							height: 64,
							mb: 2,
							borderRadius: "50%",
							backgroundColor: colors.redAccent[100],
							color: colors.redAccent[500],
						}}
					>
						<ErrorOutlineOutlinedIcon sx={{ fontSize: 36 }} />
					</Box>

					<Typography
						variant="h5"
						component="h1"
						fontWeight={600}
						color="text.primary"
						gutterBottom
					>
						<Translate String="Common.Error.Page">Page Error!</Translate>
					</Typography>

					<Typography
						variant="body1"
						color="text.secondary"
						sx={{ mb: 3, lineHeight: 1.6 }}
					>
						<Translate String="Common.Error.Page.Message">
							Oops! Looks like something went wrong here.
						</Translate>
					</Typography>

					<Box
						sx={{
							display: "flex",
							gap: 1.5,
							flexWrap: "wrap",
							justifyContent: "center",
						}}
					>
						<Button
							ref={tryAgainRef}
							variant="contained"
							color="error"
							onClick={reset}
							size="large"
							sx={{ textTransform: "none", fontWeight: 600, px: 3 }}
							aria-keyshortcuts="R"
						>
							<Translate String="Common.Error.Page.TryAgain">
								Try again
							</Translate>
						</Button>
						<Button
							variant="outlined"
							onClick={handleGoBack}
							size="large"
							startIcon={<ArrowBackIcon />}
							sx={{ textTransform: "none" }}
							aria-keyshortcuts="Escape"
						>
							<Translate String="Auto.Shared.Ui.Errors.PageError.L200C8.GoBack">Go back</Translate>
						</Button>
					</Box>
				</Box>

				{/* Developer details */}
				<Box sx={{ width: "100%", flexShrink: 0 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 0.5,
							mb: 1,
						}}
					>
						<Button
							size="small"
							onClick={() => setShowDetails((v) => !v)}
							endIcon={
								showDetails ? (
									<ExpandLessIcon fontSize="small" />
								) : (
									<ExpandMoreIcon fontSize="small" />
								)
							}
							sx={{ color: "text.secondary", textTransform: "none" }}
						>
							<Translate String="Common.Error.Page.Details">
								Developer details:
							</Translate>
						</Button>
						<Tooltip title={copied ? "Copied!" : "Copy URL and details"}>
							<IconButton
								size="small"
								onClick={() => {
									void handleCopy();
								}}
								sx={{ color: "text.secondary" }}
								aria-label={GetLanguageValueAsString(translation, "Auto.Shared.Ui.Errors.PageError.L240C20.CopyURLAndDeveloperDetails", "Copy URL and developer details")}
							>
								<ContentCopyIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
					<Collapse in={showDetails}>
						<Box
							component="pre"
							sx={{
								p: 2,
								borderRadius: 1,
								fontSize: "0.75rem",
								maxHeight: 280,
								overflowY: "scroll",
								overflowX: "auto",
								textAlign: "left",
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								backgroundColor: (t) =>
									t.palette.mode === "dark"
										? "rgba(255,255,255,0.05)"
										: "rgba(0,0,0,0.04)",
								color: "text.secondary",
								border: 1,
								borderColor: "divider",
							}}
						>
							{errorMessage && componentStack
								? `${errorMessage}\n\n${componentStack}`
								: errorMessage ||
									componentStack ||
									"No additional details available."}
						</Box>
					</Collapse>
				</Box>

				{/* Caption + Report Bug */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 1.5,
						mt: 4,
						width: "100%",
						flexShrink: 0,
					}}
				>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ opacity: 0.9 }}
					>
						<Translate String="Common.Error.Page.Stack">
							If the error persist, please submit a ticket by clicking the
							button in the bottom right corner.
						</Translate>
					</Typography>
					<Button
						size="small"
						variant="outlined"
						startIcon={<BugReportOutlinedIcon fontSize="small" />}
						onClick={() => setBugDialogOpen(true)}
						sx={{ textTransform: "none" }}
					>
						<Translate String="SideBar.ReportBug">Report Bug</Translate>
					</Button>
				</Box>
			</Box>

			{bugDialogOpen && (
				<Suspense fallback={null}>
					<ReportBugDialog
						open={bugDialogOpen}
						onClose={() => setBugDialogOpen(false)}
						initialDescription={bugReportContext}
					/>
				</Suspense>
			)}

			<Snackbar
				open={copySnackbarOpen}
				autoHideDuration={2500}
				onClose={() => setCopySnackbarOpen(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					severity="success"
					onClose={() => setCopySnackbarOpen(false)}
					sx={{ width: "100%" }}
				>
					<Translate String="Auto.Shared.Ui.Errors.PageError.L331C6.CopiedToClipboard">Copied to clipboard.</Translate>
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default PageError;
